import { useEffect, useState } from "react";
import { useAuthContext } from "../../contexts/AuthContext";
import { db } from "../../lib/firebase";
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import { Clock, Play, BookOpen, Loader2, History, ArrowLeft } from "lucide-react";

interface WatchHistoryItem {
  courseId: string;
  courseName: string;
  courseImage: string;
  lessonId: string;
  lessonName: string;
  lessonOrderIndex: number;
  actualWatchedTime: number;
  videoDuration: number;
  watchedPercentage: number;
  lastUpdated: any;
}

const CACHE_DURATION = 5 * 60 * 1000;
const cacheStore: Record<string, { data: any; timestamp: number }> = {};

function getCached(key: string) {
  const cached = cacheStore[key];
  if (!cached) return null;
  if (Date.now() - cached.timestamp > CACHE_DURATION) {
    delete cacheStore[key];
    return null;
  }
  return cached.data;
}

function setCached(key: string, data: any) {
  cacheStore[key] = { data, timestamp: Date.now() };
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatDate(timestamp: any): string {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat('th-TH', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

export default function WatchHistory() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [history, setHistory] = useState<WatchHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadHistory() {
      if (!user) return;

      const cacheKey = `watch_history_${user.uid}`;
      const cached = getCached(cacheKey);

      if (cached) {
        setHistory(cached);
        setLoading(false);
        return;
      }

      try {
        const historyItems: WatchHistoryItem[] = [];
        const purchasedCourses = user.purchasedCourses || [];

        for (const courseId of purchasedCourses) {
          const progressQuery = query(
            collection(db, "progress"),
            where("userId", "==", user.uid),
            where("courseId", "==", courseId),
            where("completed", "==", false),
            orderBy("lastUpdated", "desc"),
            limit(1)
          );

          const progressSnap = await getDocs(progressQuery);
          
          if (!progressSnap.empty) {
            const progressDoc = progressSnap.docs[0];
            const progressData = progressDoc.data();

            const courseDoc = await getDoc(doc(db, "courses", courseId));
            if (!courseDoc.exists()) continue;
            const courseData = courseDoc.data();

            const lessonDoc = await getDoc(doc(db, "lessons", progressData.lessonId));
            if (!lessonDoc.exists()) continue;
            const lessonData = lessonDoc.data();

            historyItems.push({
              courseId,
              courseName: courseData.title || "ไม่มีชื่อ",
              courseImage: courseData.thumbnail || "",
              lessonId: progressData.lessonId,
              lessonName: lessonData.title || "ไม่มีชื่อ",
              lessonOrderIndex: lessonData.orderIndex || 0,
              actualWatchedTime: progressData.actualWatchedTime || 0,
              videoDuration: lessonData.videoDuration || 0,
              watchedPercentage: progressData.watchedPercentage || 0,
              lastUpdated: progressData.lastUpdated
            });
          }
        }

        historyItems.sort((a, b) => {
          const timeA = a.lastUpdated?.toMillis ? a.lastUpdated.toMillis() : 0;
          const timeB = b.lastUpdated?.toMillis ? b.lastUpdated.toMillis() : 0;
          return timeB - timeA;
        });

        setCached(cacheKey, historyItems);
        setHistory(historyItems);
      } catch (error) {
        console.error("Error loading watch history:", error);
      } finally {
        setLoading(false);
      }
    }

    loadHistory();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">🔐</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">กรุณาเข้าสู่ระบบ</h2>
          <p className="text-gray-600 mb-6">เพื่อดูประวัติการเรียนของคุณ</p>
          <button
            onClick={() => navigate("/login")}
            className="bg-gradient-primary text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
          >
            เข้าสู่ระบบ
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">กำลังโหลดประวัติการดู...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-semibold transition-colors">
          <ArrowLeft className="w-5 h-5" />
          กลับไปหน้าหลัก
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <History className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-800">ประวัติการดู</h1>
          </div>
          <p className="text-gray-600">ดูต่อจากที่ค้างไว้ในแต่ละคอร์ส</p>
        </div>

        {history.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-lg p-12 text-center">
            <BookOpen className="w-20 h-20 text-gray-300 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-800 mb-2">ยังไม่มีประวัติการดู</h3>
            <p className="text-gray-600 mb-6">เริ่มเรียนคอร์สแรกของคุณเลย!</p>
            <Link to="/my-courses">
              <button className="bg-gradient-primary text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all">
                ไปที่คอร์สของฉัน
              </button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {history.map((item) => (
              <div
                key={`${item.courseId}-${item.lessonId}`}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row gap-6">
                    <div className="lg:w-48 flex-shrink-0">
                      <img
                        src={item.courseImage || "/placeholder-course.jpg"}
                        alt={item.courseName}
                        className="w-full h-32 lg:h-full object-cover rounded-xl"
                      />
                    </div>

                    <div className="flex-1">
                      <div className="mb-3">
                        <h3 className="text-xl font-bold text-gray-800 mb-1">
                          {item.courseName}
                        </h3>
                        <p className="text-blue-600 font-semibold flex items-center gap-2">
                          <Play className="w-4 h-4" />
                          EP.{item.lessonOrderIndex} - {item.lessonName}
                        </p>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>
                            ดูค้างไว้ที่ {formatTime(item.actualWatchedTime)} / {formatTime(item.videoDuration)}
                          </span>
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                            {item.watchedPercentage}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          อัพเดทล่าสุด: {formatDate(item.lastUpdated)}
                        </p>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                        <div
                          className="bg-gradient-primary h-2 rounded-full transition-all"
                          style={{ width: `${item.watchedPercentage}%` }}
                        ></div>
                      </div>

                      <Link to={`/courses/${item.courseId}/learn/${item.lessonOrderIndex}`}>
                        <button className="bg-gradient-primary text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2">
                          <Play className="w-5 h-5" />
                          ดูต่อ
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
